import { useState, useRef, useEffect } from 'react';
import { Plus, Calendar, Edit2, Trash2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AttachmentsStrip } from '@/features/attachments';
import { useCreateDeliverable, useToggleDeliverable, useUpdateDeliverable, useDeleteDeliverable } from '../hooks/useDeliverables';
import type { Deliverable } from '../types';

interface DeliverableListProps {
  rockId: string;
  deliverables: Deliverable[];
}

function formatDeliverableDate(dateStr: string | null): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

function isPastDate(dateStr: string | null): boolean {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export function DeliverableList({ rockId, deliverables }: DeliverableListProps) {
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const createDeliverable = useCreateDeliverable();
  const updateDeliverable = useUpdateDeliverable();
  const deleteDeliverable = useDeleteDeliverable();
  const toggleDeliverable = useToggleDeliverable();

  useEffect(() => {
    if (showAdd) inputRef.current?.focus();
  }, [showAdd]);

  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingId]);

  const handleAdd = () => {
    const title = newTitle.trim();
    if (!title) return;
    createDeliverable.mutate(
      { rockId, data: { title, sort_order: deliverables.length } },
      {
        onSuccess: () => {
          setNewTitle('');
          setShowAdd(false);
        },
      }
    );
  };

  const handleStartEdit = (deliverable: Deliverable) => {
    setEditingId(deliverable.id);
    setEditTitle(deliverable.title);
    setEditDescription(deliverable.description ?? '');
  };

  const handleSaveEdit = () => {
    const title = editTitle.trim();
    if (!title) return;
    const description = editDescription.trim();
    updateDeliverable.mutate(
      { id: editingId!, rockId, data: { title, description } },
      {
        onSuccess: () => {
          setEditingId(null);
          setEditTitle('');
          setEditDescription('');
        },
      }
    );
  };

  const handleDelete = (id: string) => {
    if (!confirm('Are you sure you want to delete this deliverable?')) return;
    deleteDeliverable.mutate({ id, rockId });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (editingId) {
        handleSaveEdit();
      } else {
        handleAdd();
      }
    }
    if (e.key === 'Escape') {
      if (editingId) {
        setEditingId(null);
        setEditTitle('');
        setEditDescription('');
      } else {
        setNewTitle('');
        setShowAdd(false);
      }
    }
  };

  return (
    <div className="pl-6 pr-2 py-2 space-y-1">
      {deliverables.map((d) => (
        <div key={d.id} className="group py-1.5">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={d.completed}
              onCheckedChange={() => toggleDeliverable.mutate({ id: d.id, rockId })}
              className="shrink-0"
            />
            {editingId === d.id ? (
              <Input
                ref={inputRef}
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={handleKeyDown}
                className="h-7 text-sm flex-1"
                disabled={updateDeliverable.isPending}
              />
            ) : (
              <span className={cn('flex-1 text-sm', d.completed && 'line-through text-muted-foreground')}>
                {d.title}
              </span>
            )}
            {editingId === d.id ? (
              <>
                <Button
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={handleSaveEdit}
                  disabled={updateDeliverable.isPending}
                >
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs"
                  onClick={() => {
                    setEditingId(null);
                    setEditTitle('');
                    setEditDescription('');
                  }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                {d.dueDate && (
                  <span
                    className={cn(
                      'flex items-center gap-1 text-xs shrink-0',
                      isPastDate(d.dueDate) && !d.completed
                        ? 'text-destructive'
                        : 'text-muted-foreground'
                    )}
                  >
                    <Calendar className="h-3 w-3" />
                    {formatDeliverableDate(d.dueDate)}
                  </span>
                )}
                <button
                  onClick={() => handleStartEdit(d)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground shrink-0"
                  title="Edit deliverable"
                >
                  <Edit2 className="h-3 w-3" />
                </button>
                <button
                  onClick={() => handleDelete(d.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive shrink-0"
                  title="Delete deliverable"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </>
            )}
          </div>
          {editingId === d.id ? (
            <Textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Notes (deliverable, evidence, etc.)"
              className="ml-6 mt-1 text-xs min-h-[60px]"
              disabled={updateDeliverable.isPending}
            />
          ) : (
            <>
              {d.description && (
                <p className="pl-6 mt-0.5 text-xs text-muted-foreground whitespace-pre-line">
                  {d.description}
                </p>
              )}
              <AttachmentsStrip entityType="deliverable" entityId={d.id} />
            </>
          )}
        </div>
      ))}

      {showAdd ? (
        <div className="flex items-center gap-2 py-1">
          <div className="w-4 shrink-0" />
          <Input
            ref={inputRef}
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Deliverable title..."
            className="h-7 text-sm"
          />
          <Button size="sm" className="h-7 px-2 text-xs" onClick={handleAdd} disabled={createDeliverable.isPending}>
            Add
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            onClick={() => { setShowAdd(false); setNewTitle(''); }}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-1 pl-6"
        >
          <Plus className="h-3 w-3" />
          Add Deliverable
        </button>
      )}
    </div>
  );
}
